import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { branchInitialState } from '@features/branch-management/constants';
import { getUniqueAccountsDataForFeeCategory } from '@features/branch-management/helper';
import { useLazyGetOrgAccountDetailsQuery } from '@features/branch-management/queries';
import { AmountMode } from '@features/transactions/constants';
import useCheckPermission from '@hooks/checkPermission';
import { buildQueryString } from '@utils/queryStringBuilder';
import { BankAccount } from 'types/account-detail';

type UseMarkAsPaidBankAccountParams = {
  organizationId: string;
  branchId: string;
  amountMode: AmountMode;
};

// Settlement bank account selection for non-cash manual payments
const useMarkAsPaidBankAccount = ({
  organizationId,
  branchId,
  amountMode
}: UseMarkAsPaidBankAccountParams) => {
  const [preferredAccount, setPreferredAccount] =
    useState<BankAccount>(branchInitialState);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const { currentOrg } = useSelector((state: any) => state.user);
  const { checkPermission } = useCheckPermission();

  const [getPrimaryAccountDetails, { data: primaryAccountDetails }] =
    useLazyGetOrgAccountDetailsQuery();

  const canFetchBankAccounts = checkPermission([
    UserPermissionTypes.ACCOUNT_DETAILS_VIEW
  ]);

  const isLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    (currentOrg?.type === AccountType.ORGANIZATION ||
      currentOrg?.type === AccountType.INDIVIDUAL) &&
    !currentOrg?.is_kyc_deferred;

  const showBankAccountDropdown =
    amountMode !== AmountMode.CASH &&
    orgFeatureFlags?.includes(
      FEATURE_FLAGS.MANUAL_PAYMENT_BANK_ACCOUNT_MAPPING
    ) &&
    !isLite &&
    canFetchBankAccounts;

  const availableAccounts = getUniqueAccountsDataForFeeCategory(
    primaryAccountDetails || {}
  );

  const handleAccountSelect = (accountId: string) => {
    const selectedAccount = availableAccounts.find(
      (account) => String(account.accountDetailId) === accountId
    );
    if (selectedAccount) {
      setPreferredAccount(selectedAccount);
    }
  };

  useEffect(() => {
    if (branchId && canFetchBankAccounts) {
      getPrimaryAccountDetails({
        organizationId,
        queryString: buildQueryString({ branch_id: branchId })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, canFetchBankAccounts]);

  return {
    showBankAccountDropdown,
    availableAccounts,
    preferredAccount,
    handleAccountSelect
  };
};

export default useMarkAsPaidBankAccount;
